import type {
  Candidate, Card, ExtractRequest, ExtractResponse, Lang, Note, Source,
  SourceKind, SyncPullResponse, SyncPushRequest,
} from "./types";

/** Thrown when the deployment's passphrase gate has not been satisfied. */
export class LockedError extends Error {
  constructor() {
    super("Not signed in.");
    this.name = "LockedError";
  }
}

/** Called when any request comes back locked, so the app can show the gate. */
let onLocked: (() => void) | null = null;
export function setLockedHandler(handler: () => void) {
  onLocked = handler;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });

  // An expired Cloudflare Access session does not fail the request: Access
  // answers with its own sign-in page, so fetch reports a perfectly good 200
  // carrying HTML. Without this check that surfaces as a JSON parse error,
  // which says nothing about what actually needs doing.
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("json")) {
    throw new Error(
      "Your sign-in session has expired. Reload the page to sign in again — " +
        "anything you have reviewed is saved and will upload afterwards.",
    );
  }

  const text = await response.text();
  let payload: unknown;
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`${path} returned a non-JSON response (HTTP ${response.status}).`);
  }
  if (!response.ok) {
    if (response.status === 401 && (payload as { locked?: boolean })?.locked) {
      onLocked?.();
      throw new LockedError();
    }
    const message =
      typeof payload === "object" && payload && "error" in payload
        ? String((payload as { error: unknown }).error)
        : `HTTP ${response.status}`;
    throw new Error(message);
  }
  return payload as T;
}

export const extract = (body: ExtractRequest) =>
  request<ExtractResponse>("/api/extract", { method: "POST", body: JSON.stringify(body) });

export const approve = (body: {
  lang: Lang;
  source: { kind: SourceKind; title: string; url?: string | null; episodeDate?: string | null; body?: string | null };
  candidates: Candidate[];
}) =>
  request<{ source: Source; notes: Note[]; cards: Card[] }>("/api/approve", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const capture = (lang: Lang, text: string) =>
  request<{ note: Note }>("/api/capture", { method: "POST", body: JSON.stringify({ lang, text }) });

export const enrich = (ids: string[]) =>
  request<{ enriched: number; notes: Note[] }>("/api/enrich", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });

export const updateNotes = (notes: (Partial<Note> & { id: string })[]) =>
  request<{ updated: number }>("/api/notes", {
    method: "POST",
    body: JSON.stringify({ action: "update", notes }),
  });

export const activateNotes = (ids: string[]) =>
  request<{ activated: number; cards: Card[] }>("/api/notes", {
    method: "POST",
    body: JSON.stringify({ action: "activate", ids }),
  });

export const deleteNotes = (ids: string[]) =>
  request<{ deleted: number }>("/api/notes", {
    method: "POST",
    body: JSON.stringify({ action: "delete", ids }),
  });

export const setSuspended = (ids: string[], suspended: boolean) =>
  request<Record<string, number>>("/api/notes", {
    method: "POST",
    body: JSON.stringify({ action: suspended ? "suspend" : "unsuspend", ids }),
  });

export const pull = (since: number) =>
  request<SyncPullResponse>(`/api/sync?since=${since}`);

export const push = (body: SyncPushRequest) =>
  request<{ applied: number; now: number }>("/api/sync", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const saveSettings = (settings: Record<string, unknown>) =>
  request<{ saved: number }>("/api/settings", { method: "PUT", body: JSON.stringify(settings) });

export const getSession = () =>
  request<{ required: boolean; authenticated: boolean }>("/api/session");

export const login = (passphrase: string) =>
  request<{ ok: boolean; required: boolean }>("/api/login", {
    method: "POST",
    body: JSON.stringify({ passphrase }),
  });
